import React, { useState, useEffect } from "react";

import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Article as BlogIcon,
  Close as CloseIcon,
  Phone as ContactIcon,
  Description as PageIcon,
  Person as DoctorIcon,
  Download as ExportIcon,
  InsertDriveFile as FileIcon,
  Home as HomeIcon,
  Info as AboutIcon,
  FlashOn as SetupIcon,
  MedicalServices as ServicesIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CreatePageModal } from "./components/admin/CreatePageModal";
import { CreateSiteModal } from "./components/admin/CreateSiteModal";
import { DeleteSiteModal } from "./components/admin/DeleteSiteModal";
import { DomainSetupModal } from "./components/admin/DomainSetupModal";
import { WelcomeState, EmptyState } from "./components/admin/EmptyStates";
import { PublishSuccessModal } from "./components/admin/PublishSuccessModal";
import { SiteSelector } from "./components/admin/SiteSelector";
import { SiteSettingsPanel } from "./components/admin/SiteSettingsPanel";
import { SortablePageItem } from "./components/admin/SortablePageItem";
import { PageEditorNew } from "./components/editor/PageEditorNew";
import { OnboardingWizard } from "./components/onboarding/OnboardingWizard";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { defaultTheme } from "./config/themeConstants";
import { CMSConfigProvider } from "./context/CMSConfigContext";
import { LanguageProvider } from "./context/LanguageContext";
import { isFirebaseInitialized } from "./firebase/config";
import {
  getAllSites,
  getSiteById,
  createSite,
  updateSite,
  deleteSite as deleteSiteFromDb,
  getAllPages,
  createPage,
  deletePage,
  getPageById,
  savePageContent,
  updatePage,
  getAllTemplates as getTemplates,
  syncTemplates,
  generateSiteFromTemplate,
  publishSite,
  unpublishSite,
  duplicatePage,
  reorderPages,
} from "./firebase/firestore";
import { generateWebsite } from "./firebase/generateWebsite";
import { uploadImage } from "./firebase/storage";
import { cmsApi } from "./services/cmsApi";
import {
  ClinicCMSProps,
  CMSDomainInfo,
  CreateSiteData,
  PageData,
  PageListItem,
  Site,
  SiteListItem,
  Template,
  ThemeSettings,
} from "./types";
import { exportToZip } from "./utils/exportUtils";
import { exportPageToReactZip } from "./utils/exportReactUtils";

// Suppress ResizeObserver loop error (benign warning from MUI/CraftJS)
if (typeof window !== "undefined") {
  const originalError = window.onerror;
  window.onerror = (message, ...args) => {
    if (typeof message === "string" && message.includes("ResizeObserver")) {
      return true;
    }
    return originalError ? originalError(message, ...args) : false;
  };
  window.addEventListener("error", (e) => {
    if (e.message?.includes("ResizeObserver")) {
      e.stopImmediatePropagation();
    }
  });
}

const DRAWER_WIDTH = 280;
const SIDEBAR_WIDTH = 50;

const BLANK_PAGE_CONTENT = JSON.stringify({
  ROOT: {
    type: { resolvedName: "RootContainer" },
    isCanvas: true,
    props: {},
    displayName: "Root",
    custom: {},
    hidden: false,
    nodes: [],
    linkedNodes: {},
  },
});

const PAGE_ICONS: Record<string, React.ReactNode> = {
  home: <HomeIcon />,
  about: <AboutIcon />,
  services: <ServicesIcon />,
  doctors: <DoctorIcon />,
  contact: <ContactIcon />,
  blog: <BlogIcon />,
};

const getPageIcon = (slug: string) => PAGE_ICONS[slug] || <FileIcon />;

// Lazy-load bundled templates (reduces initial bundle by ~248KB)
let cachedBundledTemplates: Template[] | null = null;
const loadBundledTemplates = async (): Promise<Template[]> => {
  if (cachedBundledTemplates) return cachedBundledTemplates;
  const [bundled, ishealthLanding] = await Promise.all([
    import("./data/bundled-templates.json"),
    import("./data/ishealth-cloud-landing.json"),
  ]);
  cachedBundledTemplates = [
    ...(bundled.default as Template[]),
    ...(ishealthLanding.default as Template[]),
  ];
  return cachedBundledTemplates;
};

// MUI Theme - Light mode
const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3b82f6",
    },
    secondary: {
      main: "#10b981",
    },
  },
  typography: {
    fontFamily: '"Sarabun", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const ClinicCMS: React.FC<ClinicCMSProps> = ({
  divisionId,
  apiToken,
  apiBaseUrl,
  // firebaseConfig is deprecated - Firebase should be initialized by host app
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  firebaseConfig: _firebaseConfig,
  demoMode = false,
  onExport,
  className,
  lineLiffId,
  officialAccountId,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  // Site state
  const [sites, setSites] = useState<SiteListItem[]>([]);
  const [currentSite, setCurrentSite] = useState<SiteListItem | null>(null);

  // Page state
  const [pages, setPages] = useState<PageListItem[]>([]);
  const [currentPage, setCurrentPage] = useState<PageData | null>(null);

  // Templates
  const [templates, setTemplates] = useState<Template[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCreatePageModal, setShowCreatePageModal] = useState(false);
  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [showDeleteSiteModal, setShowDeleteSiteModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<SiteListItem | null>(null);
  const [showPagePanel, setShowPagePanel] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentSiteDetails, setCurrentSiteDetails] = useState<Site | null>(null);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [apiDomainInfo, setApiDomainInfo] = useState<CMSDomainInfo | null>(null);
  const [apiSubdomain, setApiSubdomain] = useState<string | null>(null);
  const [apiSubdomainSuffix, setApiSubdomainSuffix] = useState<string>(""); // e.g., ".mehey.com" from API
  const [apiSiteUrl, setApiSiteUrl] = useState<string | null>(null); // full_domain from API
  const [apiSitePublished, setApiSitePublished] = useState<boolean>(false);
  const [apiLastPublished, setApiLastPublished] = useState<string | null>(null); // last_site_published from API
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [publishedSiteUrl, setPublishedSiteUrl] = useState<string | null>(null);

  // Check Firebase initialization (Firebase should be initialized by host app)
  useEffect(() => {
    if (demoMode) {
      setIsInitialized(true);
      setIsLoading(false);
      return;
    }

    // Check if Firebase is already initialized by host app
    if (!isFirebaseInitialized()) {
      setError(
        "Firebase is not initialized. Please initialize Firebase in your main application before using ClinicCMS."
      );
      setIsLoading(false);
      return;
    }

    setIsInitialized(true);
  }, [demoMode]);

  // Initialize CMS API service
  useEffect(() => {
    if (apiToken && apiBaseUrl) {
      cmsApi.initialize({
        baseUrl: apiBaseUrl,
        apiToken,
        divisionId,
      });
    }
  }, [apiToken, apiBaseUrl, divisionId]);

  // Load API site info when CMS API is configured
  useEffect(() => {
    if (cmsApi.isConfigured()) {
      refreshApiSiteInfo();
      refreshApiDomainInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken, apiBaseUrl, divisionId]);

  // Load sites and templates
  useEffect(() => {
    if (!isInitialized || demoMode) return;

    const init = async () => {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Connection timeout. Please check your Firebase configuration.")), 10000)
      );

      try {
        const [siteList, templateList] = (await Promise.race([
          Promise.all([getAllSites(divisionId), getTemplates()]),
          timeout,
        ])) as [SiteListItem[], Template[]];

        setSites(siteList);
        setTemplates(templateList);

        // Auto-select first site if exists
        if (siteList.length > 0) {
          setCurrentSite(siteList[0]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Error fetching data: " + (err instanceof Error ? err.message : String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [isInitialized, demoMode, divisionId]);

  // Load pages when site changes
  useEffect(() => {
    if (!currentSite) {
      setPages([]);
      setCurrentPage(null);
      return;
    }

    const loadPages = async () => {
      try {
        const pageList = await getAllPages(currentSite.id);
        setPages(pageList);

        // Auto-load first page if exists
        if (pageList.length > 0) {
          const firstPage = await getPageById(currentSite.id, pageList[0].id);
          if (firstPage) {
            setCurrentPage(firstPage);
            setShowPagePanel(false);
          }
        } else {
          setCurrentPage(null);
        }
      } catch (error) {
        console.error("Error loading pages:", error);
      }
    };

    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite?.id]);

  // Load site details (with domain info) when site changes
  useEffect(() => {
    if (!currentSite) {
      setCurrentSiteDetails(null);
      return;
    }

    const loadSiteDetails = async () => {
      try {
        const siteDetails = await getSiteById(currentSite.id);
        setCurrentSiteDetails(siteDetails);
      } catch (error) {
        console.error("Error loading site details:", error);
      }
    };

    loadSiteDetails();
  }, [currentSite]);

  // Fetch API domain info on mount and when site changes (when cmsApi is configured)
  useEffect(() => {
    if (!cmsApi.isConfigured()) return;

    const fetchDomainInfo = async () => {
      try {
        const domainInfo = await cmsApi.getDomain();
        // Treat empty domain as no domain
        setApiDomainInfo(domainInfo?.domain?.trim() ? domainInfo : null);
      } catch {
        setApiDomainInfo(null);
      }
    };

    fetchDomainInfo();
  }, [currentSite]);

  // Refresh sites list
  const refreshSites = async () => {
    try {
      const siteList = await getAllSites(divisionId);
      setSites(siteList);

      // Update currentSite if it still exists
      if (currentSite) {
        const updatedSite = siteList.find((s) => s.id === currentSite.id);
        if (updatedSite) {
          setCurrentSite(updatedSite);
        }
      }
    } catch (error) {
      console.error("Error refreshing sites:", error);
    }
  };

  // Refresh pages list
  const refreshPages = async () => {
    if (!currentSite) return;
    try {
      const pageList = await getAllPages(currentSite.id);
      setPages(pageList);
    } catch (error) {
      console.error("Error refreshing pages:", error);
    }
  };

  // Refresh site details (for domain info)
  const refreshSiteDetails = async () => {
    if (!currentSite) return;
    try {
      const siteDetails = await getSiteById(currentSite.id);
      setCurrentSiteDetails(siteDetails);
    } catch (error) {
      console.error("Error refreshing site details:", error);
    }
  };

  // Helper: Check if domain info has valid domain
  const hasValidDomain = (info: CMSDomainInfo | null): boolean => {
    return !!(info && info.domain && info.domain.trim());
  };

  // Helper: Update apiDomainInfo state with empty check
  const updateApiDomainState = (domainInfo: CMSDomainInfo | null) => {
    setApiDomainInfo(hasValidDomain(domainInfo) ? domainInfo : null);
  };

  // Refresh API domain info
  const refreshApiDomainInfo = async () => {
    if (!cmsApi.isConfigured()) return;
    try {
      const domainInfo = await cmsApi.getDomain();
      updateApiDomainState(domainInfo);
    } catch {
      setApiDomainInfo(null);
    }
  };

  // Refresh subdomain info
  const refreshSubdomainInfo = async () => {
    if (!cmsApi.isConfigured()) return;
    try {
      const subdomainInfo = await cmsApi.getSubdomain();
      setApiSubdomain(subdomainInfo?.subdomain || null);
      if (subdomainInfo?.subdomain_suffix) {
        setApiSubdomainSuffix(subdomainInfo.subdomain_suffix);
      }
    } catch {
      setApiSubdomain(null);
    }
  };

  // Refresh API site info (publish status, subdomain, site URL)
  const refreshApiSiteInfo = async () => {
    if (!cmsApi.isConfigured()) return;
    try {
      const siteInfo = await cmsApi.getSite();
      setApiSubdomain(siteInfo?.subdomain || null);
      if (siteInfo?.subdomain_suffix) {
        setApiSubdomainSuffix(siteInfo.subdomain_suffix);
      }
      setApiSiteUrl(siteInfo?.full_domain || null);
      setApiSitePublished(siteInfo?.is_site_published || false);
      setApiLastPublished(siteInfo?.last_site_published || null);
    } catch {
      setApiSitePublished(false);
    }
  };

  // Handle set subdomain
  const handleSetSubdomain = async (subdomain: string): Promise<void> => {
    await cmsApi.setSubdomain(subdomain);
    setApiSubdomain(subdomain);
    // Refresh to get updated full_domain
    await refreshApiSiteInfo();
  };

  // Open domain modal - refresh API info first if configured
  const handleOpenDomainModal = async () => {
    if (cmsApi.isConfigured()) {
      await refreshApiDomainInfo();
      await refreshSubdomainInfo();
    }
    setShowDomainModal(true);
  };

  const handleApiSetDomain = async (domain: string): Promise<CMSDomainInfo> => {
    const result = await cmsApi.setDomain(domain);
    setApiDomainInfo(result);
    return result;
  };

  const handleApiVerifyDomain = async (): Promise<boolean> => {
    try {
      await cmsApi.verifyDomain();
      const domainInfo = await cmsApi.getDomain();
      updateApiDomainState(domainInfo);
      return domainInfo.is_domain_verified;
    } catch {
      return false;
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm("Are you sure you want to remove this domain?")) return;

    try {
      const result = await cmsApi.removeDomain();
      updateApiDomainState(result);
      // Also clear subdomain and refresh site URL
      setApiSubdomain(null);
      await refreshApiSiteInfo();
    } catch (err) {
      console.error("Error removing domain:", err);
      throw err;
    }
  };

  // Find the site that is currently published (from Firebase)
  const publishedSite = sites.find((s) => s.isPublished && s.id !== currentSite?.id);
  const isCurrentSitePublished = currentSite?.isPublished || false;

  // Core publish logic - shared between handlePublishSite and handlePublishReplace
  const doPublishSite = async () => {
    if (!currentSite) return;

    // If CMS API is configured, use the full publish flow
    if (cmsApi.isConfigured()) {
      // 1. Get domain info for challenge (password)
      const domainInfo = await cmsApi.getDomain();

      // 2. Load all pages for export
      const allPages = await handleLoadAllPages();
      if (!allPages.length) {
        alert("No pages to publish.");
        return;
      }

      // 3. Export to ZIP with password (challenge from domain API)
      const siteTheme: ThemeSettings = {
        primary: currentSite.theme.primaryColor,
        secondary: currentSite.theme.secondaryColor,
        font: currentSite.theme.font || "Sarabun",
      };

      const zipBlob = await exportToZip(allPages, currentSite.name, siteTheme, {
        password: domainInfo.challenge,
        favicon: currentSiteDetails?.favicon,
        logo: currentSiteDetails?.logo,
        lineLiffId,
        officialAccountId,
        facebookUrl: "https://www.facebook.com/mehealthapp",
        gaMeasurementId: currentSiteDetails?.gaMeasurementId,
      });

      // 4. Unpublish previously published site in Firebase (if any)
      const previouslyPublished = sites.find((s) => s.isPublished && s.id !== currentSite.id);
      if (previouslyPublished) {
        await unpublishSite(previouslyPublished.id);
      }

      // 5. Upload to API (this also publishes the site)
      await cmsApi.uploadSite(zipBlob);

      // 6. Mark current site as published in Firebase
      await publishSite(currentSite.id);

      // 7. Get site info to know the URL and publish status
      const siteInfo = await cmsApi.getSite();
      await refreshApiDomainInfo();
      setApiSubdomain(siteInfo?.subdomain || null);
      setApiSiteUrl(siteInfo?.full_domain || null);
      setApiSitePublished(siteInfo?.is_site_published ?? true); // Upload also publishes
      setApiLastPublished(siteInfo?.last_site_published || null);

      // 8. Refresh sites list to update isPublished status
      await refreshSites();

      // Use full_domain from API (auto-generated by backend)
      // e.g., "bb45a0bb8690.mehey-pro.ishealth.app"
      const primaryUrl = siteInfo?.full_domain || "your site";

      // Show success modal
      setPublishedSiteUrl(primaryUrl);
      setShowPublishSuccess(true);
    } else {
      // Fallback: just update Firebase status
      // Unpublish previously published site first
      const previouslyPublished = sites.find((s) => s.isPublished && s.id !== currentSite.id);
      if (previouslyPublished) {
        await unpublishSite(previouslyPublished.id);
      }
      await publishSite(currentSite.id);
      await refreshSites();
      await refreshSiteDetails();
    }
  };

  // Handle publish site (when no other site is published)
  const handlePublishSite = async () => {
    if (!currentSite) return;

    setIsPublishing(true);
    try {
      await doPublishSite();
    } catch (error) {
      console.error("Error publishing site:", error);
      alert("Failed to publish site: " + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle publish replace (when another site is already published)
  const handlePublishReplace = async () => {
    if (!currentSite || !publishedSite) return;

    const confirmed = window.confirm(
      `"${publishedSite.name}" จะถูก unpublish และ "${currentSite.name}" จะถูก publish แทน\n\nต้องการดำเนินการต่อหรือไม่?`
    );
    if (!confirmed) return;

    setIsPublishing(true);
    try {
      await doPublishSite();
    } catch (error) {
      console.error("Error publishing site:", error);
      alert("Failed to publish site: " + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle domain modal close - refresh domain info
  const handleDomainModalClose = async () => {
    setShowDomainModal(false);
    const domainInfo = await cmsApi.getDomain().catch(() => null);
    const subdomainInfo = await cmsApi.getSubdomain().catch(() => null);
    updateApiDomainState(domainInfo);
    setApiSubdomain(subdomainInfo?.subdomain || null);
    if (subdomainInfo?.subdomain_suffix) {
      setApiSubdomainSuffix(subdomainInfo.subdomain_suffix);
    }
  };

  // Handle site selection
  const handleSelectSite = (site: SiteListItem) => {
    setCurrentSite(site);
    setCurrentPage(null);
    setShowPagePanel(true);
    setShowSettings(false);
  };

  // Handle create site
  const handleCreateSite = async (data: CreateSiteData) => {
    try {
      const newSite = await createSite(data);

      // Convert site theme to ThemeSettings format for pages
      const pageTheme: ThemeSettings = {
        primary: data.theme.primaryColor,
        secondary: data.theme.secondaryColor,
        font: data.theme.font || "Sarabun",
      };

      // Generate pages from template (including blank)
      if (data.templateId) {
        await generateSiteFromTemplate(
          newSite.id,
          data.templateId,
          {
            name: data.name,
            phone: data.phone || "",
          },
          pageTheme
        );
      }

      await refreshSites();

      // Select the new site
      const siteList = await getAllSites(divisionId);
      const createdSite = siteList.find((s) => s.id === newSite.id);
      if (createdSite) {
        setCurrentSite(createdSite);
      }

      setShowCreateSiteModal(false);
    } catch (error) {
      console.error("Error creating site:", error);
    }
  };

  // Handle delete site
  const handleDeleteSite = async () => {
    if (!siteToDelete) return;

    try {
      const deletedCurrentSite = currentSite?.id === siteToDelete.id;

      await deleteSiteFromDb(siteToDelete.id);
      await refreshSites();

      // Get remaining sites
      const siteList = await getAllSites(divisionId);

      // If deleted current site, select another or clear
      if (deletedCurrentSite) {
        if (siteList.length > 0) {
          setCurrentSite(siteList[0]);
          // Load pages for the new site
          const pageList = await getAllPages(siteList[0].id);
          setPages(pageList);
          setCurrentPage(null);
        } else {
          setCurrentSite(null);
          setCurrentPage(null);
          setPages([]);
        }
      }

      setShowDeleteSiteModal(false);
      setSiteToDelete(null);
    } catch (error) {
      console.error("Error deleting site:", error);
    }
  };

  // Load a page
  const loadPage = async (pageId: string) => {
    if (!currentSite) return;
    try {
      const page = await getPageById(currentSite.id, pageId);
      if (page) {
        setCurrentPage(page);
        setShowPagePanel(false);
        setShowSettings(false); // Close Site Settings when selecting a page
      }
    } catch (error) {
      console.error("Error fetching page:", error);
    }
  };

  // Handle create new page
  const handleCreatePage = async (name: string, slug: string, templateId?: string) => {
    if (!currentSite) return;

    try {
      let templateContent = BLANK_PAGE_CONTENT; // Use default blank page content
      if (templateId) {
        const template = templates.find((t) => t.id === templateId);
        if (template) templateContent = template.content;
      }

      const newPage = await createPage(currentSite.id, name, slug, templateContent);
      await refreshPages();
      // Update page count locally (don't refreshSites — it triggers useEffect that resets currentPage)
      setSites((prev) => prev.map((s) => s.id === currentSite.id ? { ...s, pageCount: (s.pageCount || 0) + 1 } : s));
      setCurrentPage(newPage);
      setShowPagePanel(false);
      setShowCreatePageModal(false);
    } catch (error) {
      console.error("Error creating page:", error);
    }
  };

  // Handle delete page
  const handleDeletePage = async (pageId: string, pageName: string) => {
    if (!currentSite) return;
    if (!window.confirm(`Delete "${pageName}"? This cannot be undone.`)) return;

    try {
      await deletePage(currentSite.id, pageId);
      if (currentPage?.id === pageId) setCurrentPage(null);
      await refreshPages();
      await refreshSites(); // Update page count
    } catch (error) {
      console.error("Error deleting page:", error);
    }
  };

  // Rename page (name + slug)
  const handleRenamePage = async (pageId: string, name: string, slug: string) => {
    if (!currentSite) return;

    try {
      await updatePage(currentSite.id, pageId, { name, slug });
      // Update current page if it's the one being renamed
      if (currentPage?.id === pageId) {
        setCurrentPage({ ...currentPage, name, slug });
      }
      await refreshPages();
    } catch (error) {
      console.error("Error renaming page:", error);
    }
  };

  // Save page content
  const handleSave = async (json: string) => {
    if (!currentSite || !currentPage) return;

    try {
      await savePageContent(currentSite.id, currentPage.id, json);
      setCurrentPage({ ...currentPage, content: json });
      await refreshPages();
    } catch (error) {
      console.error("Error saving page:", error);
      throw error;
    }
  };

  // Update page metadata
  const handleUpdatePage = async (updates: Partial<PageData>) => {
    if (!currentSite || !currentPage) return;

    try {
      await updatePage(currentSite.id, currentPage.id, updates);
      setCurrentPage({ ...currentPage, ...updates });
      await refreshPages();
    } catch (error) {
      console.error("Error updating page:", error);
      throw error;
    }
  };

  // Handle theme change from editor - sync to site
  const handleThemeChange = async (theme: ThemeSettings) => {
    if (!currentSite) return;

    try {
      // Convert ThemeSettings to Site.theme format and update site
      await updateSite(currentSite.id, {
        theme: {
          primaryColor: theme.primary,
          secondaryColor: theme.secondary,
          font: theme.font,
        },
      });
      // Update local state
      setCurrentSite({
        ...currentSite,
        theme: {
          primaryColor: theme.primary,
          secondaryColor: theme.secondary,
          font: theme.font,
        },
      });
      await refreshSites();
    } catch (error) {
      console.error("Error syncing theme to site:", error);
    }
  };

  // Load page by slug (for preview navigation)
  const handleLoadPage = async (slug: string): Promise<PageData | null> => {
    if (!currentSite) return null;

    try {
      // Find page by slug
      const pageItem = pages.find((p) => p.slug === slug);
      if (!pageItem) return null;

      // Fetch full page data
      const page = await getPageById(currentSite.id, pageItem.id);
      return page;
    } catch (error) {
      console.error("Error loading page:", error);
      return null;
    }
  };

  // Load all pages (for preview pre-fetch)
  const handleLoadAllPages = async (): Promise<PageData[]> => {
    if (!currentSite) return [];

    try {
      const allPages = await Promise.all(pages.map((p) => getPageById(currentSite.id, p.id)));
      return allPages.filter((p): p is PageData => p !== null);
    } catch (error) {
      console.error("Error loading all pages:", error);
      return [];
    }
  };

  // Handle onboarding completion - creates a NEW site
  const handleOnboardingComplete = async (data: {
    clinicInfo: { clinicName: string; phone: string; email: string; address: string; lineId: string; logoUrl: string };
    theme: { primaryColor: string; secondaryColor: string; template: "clean" | "premium" };
    pages: { home: boolean; services: boolean; doctors: boolean; about: boolean; contact: boolean; blog: boolean };
  }) => {
    try {
      // 1. Create a NEW site first
      const newSiteData: CreateSiteData = {
        divisionId,
        name: data.clinicInfo.clinicName || "My Website",
        slug: (data.clinicInfo.clinicName || "my-website").toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        phone: data.clinicInfo.phone || "",
        logo: data.clinicInfo.logoUrl || "",
        theme: {
          primaryColor: data.theme.primaryColor,
          secondaryColor: data.theme.secondaryColor,
        },
        templateId: data.theme.template,
      };

      const newSite = await createSite(newSiteData);

      // 2. Generate pages for the NEW site
      const result = await generateWebsite(newSite.id, data);

      if (result.success) {
        setShowOnboarding(false);

        // 3. Refresh sites list first
        const siteList = await getAllSites(divisionId);
        setSites(siteList);

        // 4. Set the new site as current (use fresh data from list)
        const freshNewSite = siteList.find((s) => s.id === newSite.id) || newSite;
        setCurrentSite(freshNewSite);

        // 5. Refresh pages for the new site
        const pageList = await getAllPages(newSite.id);
        setPages(pageList);

        // 6. Load home page if created
        const homePage = result.pages?.find((p: { slug: string }) => p.slug === "home");
        if (homePage) loadPage(homePage.id);
      }
    } catch (error) {
      console.error("Error generating website:", error);
    }
  };

  // Export handler - generates static HTML and downloads as ZIP
  const handleExport = async () => {
    if (!currentSite) {
      alert("Please select a site first.");
      return;
    }

    const publishedPages = pages.filter((p) => p.isPublished);
    if (publishedPages.length === 0) {
      alert("No published pages to export. Please publish at least one page first.");
      return;
    }

    if (!window.confirm(`Export ${publishedPages.length} published page(s) as static HTML website?`)) {
      return;
    }

    try {
      // Show loading state
      const loadingMessage = "Generating static website...";
      console.log(loadingMessage);

      // Get full page data with content for each published page
      const fullPages: PageData[] = [];
      for (const page of publishedPages) {
        const fullPage = await getPageById(currentSite.id, page.id);
        if (fullPage) {
          fullPages.push(fullPage);
        }
      }

      if (fullPages.length === 0) {
        alert("Error loading page content. Please try again.");
        return;
      }

      // Get site theme (use first page's theme or default)
      // Use the same defaultTheme as the editor to ensure consistency between preview and export
      const siteTheme = fullPages[0]?.theme || defaultTheme;

      // Ask for password (optional)
      const password = window.prompt("Enter password to protect ZIP file (leave empty for no password):", "");

      // User cancelled
      if (password === null) {
        return;
      }

      // Generate ZIP file with optional password
      const zipBlob = await exportToZip(fullPages, currentSite.name, siteTheme, {
        password: password || undefined,
        favicon: currentSiteDetails?.favicon,
        logo: currentSiteDetails?.logo,
        lineLiffId,
        officialAccountId,
        facebookUrl: "https://www.facebook.com/mehealthapp",
        gaMeasurementId: currentSiteDetails?.gaMeasurementId,
      });

      // If onExport callback is provided, use it
      if (onExport) {
        onExport(zipBlob);
      } else {
        // Otherwise, trigger browser download
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${currentSite.slug || "website"}-export.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      alert(`Successfully exported ${fullPages.length} page(s)!`);
    } catch (error) {
      console.error("Error exporting website:", error);
      alert("Error exporting website. Please try again.");
    }
  };

  // Export current page as React TSX components
  const handleExportReact = async () => {
    if (!currentPage || !currentSite || isExporting) return;
    if (!currentPage.content) {
      alert("This page has no content to export.");
      return;
    }

    setIsExporting(true);
    try {
      const siteTheme: ThemeSettings = currentPage.theme || {
        primary: currentSite.theme.primaryColor,
        secondary: currentSite.theme.secondaryColor,
        font: currentSite.theme.font || "Sarabun",
      };

      const zipBlob = await exportPageToReactZip(
        currentPage.content,
        currentPage.slug,
        siteTheme,
      );

      // Trigger download
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${currentPage.slug}-react.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting as React:", error);
      alert("Error exporting as React components. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Sync templates (lazy-loads bundled templates - works in CRA/Vite/Next.js)
  const handleSyncTemplates = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    try {
      // Lazy-load templates
      const bundledTemplates = await loadBundledTemplates();

      if (!window.confirm(`Sync ${bundledTemplates.length} templates to Firebase?`)) {
        setIsSyncing(false);
        return;
      }

      const result = await syncTemplates(bundledTemplates);
      if (result.errors.length > 0) {
        alert(`Synced ${result.synced} templates with ${result.errors.length} errors.`);
      } else {
        alert(`Successfully synced ${result.synced} templates!`);
      }

      const templateList = await getTemplates();
      setTemplates(templateList);
    } catch (error) {
      console.error("Error syncing templates:", error);
      alert("Failed to sync templates.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Duplicate page handler
  const handleDuplicatePage = async (pageId: string) => {
    if (!currentSite) return;
    try {
      const newPage = await duplicatePage(currentSite.id, pageId);
      await refreshPages();
      // Load the new duplicated page
      setCurrentPage(newPage);
    } catch (err) {
      console.error("Failed to duplicate page:", err);
      alert("Failed to duplicate page. Please try again.");
    }
  };

  // Reorder pages handler
  const handleReorderPages = async (newOrder: string[]) => {
    if (!currentSite) return;
    try {
      await reorderPages(currentSite.id, newOrder);
      await refreshPages();
    } catch (err) {
      console.error("Failed to reorder pages:", err);
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px drag before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pages.findIndex((p) => p.id === active.id);
      const newIndex = pages.findIndex((p) => p.id === over.id);

      const newPages = arrayMove(pages, oldIndex, newIndex);
      setPages(newPages);

      // Persist to database
      handleReorderPages(newPages.map((p) => p.id));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box
          className={className}
          sx={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <CircularProgress size={48} />
          <Typography color="text.secondary">Loading CMS...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Error state
  if (error) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box
          className={className}
          sx={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            flexDirection: "column",
            gap: 3,
            p: 4,
          }}
        >
          <Typography color="error.main" variant="h6">
            Error
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 500, textAlign: "center" }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setError(null);
              setIsLoading(true);
              setIsInitialized(false);
            }}
          >
            Retry
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  // No sites - show welcome screen
  if (sites.length === 0) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box className={className} sx={{ height: "100vh" }}>
          <WelcomeState onCreateSite={() => setShowCreateSiteModal(true)} />

          {showCreateSiteModal && (
            <CreateSiteModal
              divisionId={divisionId}
              templates={templates}
              onClose={() => setShowCreateSiteModal(false)}
              onCreate={handleCreateSite}
            />
          )}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <CMSConfigProvider config={{ lineLiffId, officialAccountId, apiToken, apiBaseUrl, divisionId }}>
      <LanguageProvider>
        <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <Box className={className} sx={{ display: "flex", height: "calc(100vh - 40px)", bgcolor: "grey.100", position: "relative" }}>
          {/* Mini Sidebar */}
          <Paper
            elevation={0}
            sx={{
              minWidth: SIDEBAR_WIDTH,
              bgcolor: "white",
              borderRight: 1,
              borderColor: "grey.300",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 1.5,
            }}
          >
            {/* Nav Buttons */}
            <Stack spacing={0.5} alignItems="center">
              <Tooltip title="Pages" placement="right">
                <span>
                  <IconButton
                    onClick={() => setShowPagePanel(!showPagePanel)}
                    disabled={!currentSite}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      color: showPagePanel ? "white" : "grey.600",
                      bgcolor: showPagePanel ? "primary.main" : "transparent",
                      "&:hover": {
                        bgcolor: showPagePanel ? "primary.dark" : alpha("#000", 0.04),
                      },
                      "&.Mui-disabled": { color: "grey.400" },
                    }}
                  >
                    <PageIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="New Page" placement="right">
                <span>
                  <IconButton
                    onClick={() => setShowCreatePageModal(true)}
                    disabled={!currentSite}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      color: "white",
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      },
                      "&.Mui-disabled": {
                        background: "grey.300",
                        color: "grey.500",
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Quick Setup" placement="right">
                <span>
                  <Badge color="warning" variant="dot" invisible={pages.length > 0}>
                    <IconButton
                      onClick={() => setShowOnboarding(true)}
                      disabled={!currentSite}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        color: "grey.600",
                        "&:hover": { bgcolor: alpha("#000", 0.04), color: "grey.800" },
                        "&.Mui-disabled": { color: "grey.400" },
                      }}
                    >
                      <SetupIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Badge>
                </span>
              </Tooltip>
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack spacing={0.5} alignItems="center">
              <Tooltip title="Site Settings" placement="right">
                <span>
                  <IconButton
                    onClick={() => {
                      setShowSettings(true);
                      setCurrentPage(null);
                      setShowPagePanel(false);
                    }}
                    disabled={!currentSite}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      color: showSettings ? "white" : "grey.600",
                      bgcolor: showSettings ? "primary.main" : "transparent",
                      "&:hover": {
                        bgcolor: showSettings ? "primary.dark" : alpha("#000", 0.04),
                        color: showSettings ? "white" : "grey.800",
                      },
                      "&.Mui-disabled": { color: "grey.400" },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Sync Templates to Firebase" placement="right">
                <span>
                  <IconButton
                    onClick={handleSyncTemplates}
                    disabled={isSyncing}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      color: "grey.600",
                      "&:hover": { bgcolor: alpha("#000", 0.04), color: "grey.800" },
                      "&.Mui-disabled": { color: "grey.400" },
                    }}
                  >
                    {isSyncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Export" placement="right">
                <span>
                  <IconButton
                    onClick={handleExport}
                    disabled={!currentSite || pages.length === 0}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      color: "grey.600",
                      "&:hover": { bgcolor: alpha("#000", 0.04), color: "grey.800" },
                      "&.Mui-disabled": { color: "grey.400" },
                    }}
                  >
                    <ExportIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>

          {/* Page Panel - Absolute position relative to parent */}
          <Box
            sx={{
              position: "absolute",
              left: SIDEBAR_WIDTH,
              top: 0,
              bottom: 0,
              width: DRAWER_WIDTH,
              zIndex: 1200,
              overflow: "hidden",
              pointerEvents: showPagePanel && currentSite ? "auto" : "none",
            }}
          >
            <Slide direction="right" in={showPagePanel && !!currentSite} mountOnEnter unmountOnExit>
              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "white",
                  borderRight: 1,
                  borderColor: "grey.300",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Site Selector Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "grey.200" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <SiteSelector
                      sites={sites}
                      currentSite={currentSite}
                      onSelectSite={handleSelectSite}
                      onCreateSite={() => setShowCreateSiteModal(true)}
                      onDeleteSite={(site) => {
                        setSiteToDelete(site);
                        setShowDeleteSiteModal(true);
                      }}
                      onRenameSite={async (site, newName) => {
                        await updateSite(site.id, { name: newName });
                        setSites((prev) => prev.map((s) => s.id === site.id ? { ...s, name: newName } : s));
                        if (currentSite?.id === site.id) {
                          setCurrentSite((prev) => prev ? { ...prev, name: newName } : prev);
                        }
                      }}
                    />
                    <IconButton size="small" onClick={() => setShowPagePanel(false)} sx={{ color: "grey.600" }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreatePageModal(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.25,
                      background: `linear-gradient(135deg, ${currentSite?.theme.primaryColor || "#3B82F6"} 0%, ${currentSite?.theme.secondaryColor || "#2563EB"} 100%)`,
                      boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
                      "&:hover": {
                        opacity: 0.9,
                      },
                    }}
                  >
                    New Page
                  </Button>
                </Box>

                {/* Page List */}
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  {pages.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <PageIcon sx={{ color: "grey.400", fontSize: 28 }} />
                      </Box>
                      <Typography color="text.secondary" gutterBottom>
                        No pages yet
                      </Typography>
                      <Button size="small" onClick={() => setShowCreatePageModal(true)} sx={{ color: "primary.main" }}>
                        Create your first page
                      </Button>
                    </Box>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={pages.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                        <List sx={{ p: 1 }}>
                          {pages.map((page) => (
                            <SortablePageItem
                              key={page.id}
                              page={page}
                              isSelected={currentPage?.id === page.id}
                              primaryColor={currentSite?.theme.primaryColor || "#3B82F6"}
                              icon={getPageIcon(page.slug)}
                              onSelect={() => loadPage(page.id)}
                              onDelete={() => handleDeletePage(page.id, page.name)}
                              onDuplicate={() => handleDuplicatePage(page.id)}
                              onRename={(name, slug) => handleRenamePage(page.id, name, slug)}
                            />
                          ))}
                        </List>
                      </SortableContext>
                    </DndContext>
                  )}
                </Box>

                {/* Footer Stats */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: "grey.200" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="caption" color="text.secondary">
                      {pages.length} page{pages.length !== 1 ? "s" : ""}
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      {pages.filter((p) => p.isPublished).length} published
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Slide>
          </Box>

          {/* Main Editor Area */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              ml: showPagePanel && currentSite ? `${DRAWER_WIDTH}px` : 0,
              transition: "margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {showSettings && currentSiteDetails ? (
              <SiteSettingsPanel
                site={currentSiteDetails}
                onBack={() => {
                  setShowSettings(false);
                  if (pages.length > 0) {
                    setShowPagePanel(true);
                  }
                }}
                apiDomain={apiDomainInfo}
                onSetDomain={handleApiSetDomain}
                onVerifyDomain={handleApiVerifyDomain}
                onRemoveDomain={handleRemoveDomain}
                subdomain={apiSubdomain}
                subdomainSuffix={apiSubdomainSuffix}
                onSetSubdomain={handleSetSubdomain}
                siteUrl={apiSiteUrl}
                isSitePublished={apiSitePublished}
                isCurrentSitePublished={isCurrentSitePublished}
                publishedSiteName={publishedSite?.name || null}
                onPublish={handlePublishSite}
                onPublishReplace={handlePublishReplace}
                onUploadFavicon={uploadImage}
                onUploadLogo={uploadImage}
                onUpdateSite={async (updates) => {
                  if (currentSite) {
                    await updateSite(currentSite.id, updates);
                    await refreshSiteDetails();
                  }
                }}
              />
            ) : currentPage ? (
              <ThemeProvider theme={lightTheme}>
                <PageEditorNew
                  key={currentPage.id}
                  page={currentPage}
                  onSave={handleSave}
                  onUpdatePage={handleUpdatePage}
                  onExportReact={handleExportReact}
                  isExporting={isExporting}
                  onThemeChange={handleThemeChange}
                  siteTheme={
                    currentSite
                      ? {
                          primary: currentSite.theme.primaryColor,
                          secondary: currentSite.theme.secondaryColor,
                          font: currentSite.theme.font || "Sarabun",
                        }
                      : undefined
                  }
                  pages={pages}
                  onLoadPage={handleLoadPage}
                  onLoadAllPages={handleLoadAllPages}
                  hasDomain={
                    cmsApi.isConfigured() ? !!apiDomainInfo?.domain : !!currentSiteDetails?.domain?.customDomain
                  }
                  isDomainVerified={
                    cmsApi.isConfigured() ? !!apiDomainInfo?.is_domain_verified : !!currentSiteDetails?.domain?.verified
                  }
                  isSitePublished={cmsApi.isConfigured() ? apiSitePublished : !!currentSiteDetails?.isPublished}
                  isCurrentSitePublished={isCurrentSitePublished}
                  publishedSiteName={publishedSite?.name || null}
                  onShowDomainModal={handleOpenDomainModal}
                  onPublishSite={handlePublishSite}
                  onPublishReplace={handlePublishReplace}
                  isPublishing={isPublishing}
                  siteUrl={apiSiteUrl}
                  lastPublished={apiLastPublished}
                  onUnpublishSite={async () => {
                    if (currentSite && window.confirm("Unpublish site? It will no longer be accessible.")) {
                      setIsPublishing(true);
                      try {
                        if (cmsApi.isConfigured()) {
                          await cmsApi.setSitePublished(false);
                          setApiSitePublished(false);
                        }
                        await unpublishSite(currentSite.id);
                        await refreshSites();
                        await refreshSiteDetails();
                      } finally {
                        setIsPublishing(false);
                      }
                    }
                  }}
                />
              </ThemeProvider>
            ) : currentSite ? (
              <EmptyState
                hasPages={pages.length > 0}
                onOpenPages={() => setShowPagePanel(true)}
                onQuickSetup={() => setShowOnboarding(true)}
                onCreatePage={() => setShowCreatePageModal(true)}
              />
            ) : (
              <WelcomeState onCreateSite={() => setShowCreateSiteModal(true)} />
            )}
          </Box>

          {/* Modals */}
          {showCreatePageModal && currentSite && (
            <CreatePageModal
              templates={templates}
              onClose={() => setShowCreatePageModal(false)}
              onCreate={handleCreatePage}
            />
          )}

          {showCreateSiteModal && (
            <CreateSiteModal
              divisionId={divisionId}
              templates={templates}
              onClose={() => setShowCreateSiteModal(false)}
              onCreate={handleCreateSite}
            />
          )}

          {showDeleteSiteModal && siteToDelete && (
            <DeleteSiteModal
              site={siteToDelete}
              onClose={() => {
                setShowDeleteSiteModal(false);
                setSiteToDelete(null);
              }}
              onDelete={handleDeleteSite}
            />
          )}

          {showOnboarding && currentSite && (
            <OnboardingWizard onComplete={handleOnboardingComplete} onCancel={() => setShowOnboarding(false)} />
          )}

          {showDomainModal && currentSite && (
            <DomainSetupModal
              key={apiDomainInfo?.id || "new"}
              apiDomain={apiDomainInfo}
              onClose={handleDomainModalClose}
              onSetDomain={handleApiSetDomain}
              onVerifyDomain={handleApiVerifyDomain}
              onRemove={apiDomainInfo?.domain ? handleRemoveDomain : undefined}
              subdomain={apiSubdomain}
              subdomainSuffix={apiSubdomainSuffix}
              onSetSubdomain={handleSetSubdomain}
              defaultSubdomain={currentSite.slug || currentSite.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              isSitePublished={apiSitePublished}
              onPublish={handlePublishSite}
            />
          )}

          {/* Publish Success Modal */}
          {showPublishSuccess && publishedSiteUrl && (
            <PublishSuccessModal
              siteUrl={publishedSiteUrl}
              customDomain={apiDomainInfo?.is_domain_verified ? apiDomainInfo.domain : null}
              onClose={() => setShowPublishSuccess(false)}
              onAddCustomDomain={handleOpenDomainModal}
            />
          )}
        </Box>
      </ErrorBoundary>
        </ThemeProvider>
      </LanguageProvider>
    </CMSConfigProvider>
  );
};

export default ClinicCMS;
